import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";

interface Course {
  id: string;
  courseName: string;
  courseCode: string;
  level: string;
  fieldOfStudy: string[];
  modeOfStudy: string;
  programType: string;
  startDate: string;
  durationDay: string;
  estimatedTotalCourseFee: string;
}

interface CompareContextType {
  compareCourses: Course[];
  addToCompare: (course: Course) => void;
  removeFromCompare: (courseId: string) => void;
  isInCompare: (courseId: string) => boolean;
  isCompareFull: () => boolean;
  clearCompareCourses: () => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

interface CompareProviderProps {
  children: ReactNode;
}

export const useCompare = (): CompareContextType => {
  const context = useContext(CompareContext);
  if (context === undefined) {
    throw new Error("useCompare must be used within a CompareProvider");
  }
  return context;
};

export const CompareProvider: React.FC<CompareProviderProps> = ({
  children,
}) => {
  const [compareCourses, setCompareCourses] = useState<Course[]>(() => {
    const savedCourses = localStorage.getItem("compareCourses");
    return savedCourses ? JSON.parse(savedCourses) : [];
  });

  useEffect(() => {
    localStorage.setItem("compareCourses", JSON.stringify(compareCourses));
  }, [compareCourses]);

  const addToCompare = (course: Course) => {
    setCompareCourses((prev) => [...prev, course]);
  };

  const removeFromCompare = (courseId: string) => {
    setCompareCourses((prev) =>
      prev.filter((course) => course.id !== courseId)
    );
  };

  const isInCompare = (courseId: string): boolean => {
    return compareCourses.some((course) => course.id === courseId);
  };

  const isCompareFull = (): boolean => {
    return compareCourses.length >= 3;
  };

  const clearCompareCourses = () => {
    setCompareCourses([]);
    localStorage.removeItem("compareCourses");
  };

  return (
    <CompareContext.Provider
      value={{
        compareCourses,
        addToCompare,
        removeFromCompare,
        isInCompare,
        isCompareFull,
        clearCompareCourses,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export default CompareProvider;
